// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * How a recipe's ingredient quantity is PRINTED (founder, launch fix 2026-10-13: "Zwiebel 0,8 Stk."
 * must never appear). DISPLAY only — the served number is never changed anywhere else: the portion
 * owner's scaling, the shopping list, the pantry and the nutrition all keep the exact quantity;
 * only the line a person reads at the stove is rounded to something a kitchen can do.
 *
 * This file is byte-identical in mobile/src/lib and frontend/src/lib (no imports, so it cannot
 * drift in form — `ingredient-quantity-parity` in the mobile suite compares the two files).
 *
 * Counted units: the catalog's canonical `count` (an onion, an egg) and `clove` (garlic). They are
 * scaled by servings and so become fractional (1 onion x 0.75 portions = 0.75). Nobody cooks with
 * 0.8 of an egg, so:
 *
 *  - a naturally halvable item (HALVABLE_COUNT_INGREDIENTS: a half onion, half an avocado, half a
 *    lemon are normal kitchen amounts) rounds to the NEAREST HALF and never below one half:
 *    0.3 -> "½", 0.8 -> "1", 1.4 -> "1½", 2.6 -> "2½". A half is the smallest honest amount the
 *    kitchen can measure; showing "1" for a recipe that really asks for half a lemon would
 *    double it.
 *  - every other counted item (an egg, a garlic clove, a tortilla — WHOLE_COUNT_INGREDIENTS, and any
 *    ingredient this list does not know) rounds to the NEAREST WHOLE PIECE and never below one:
 *    0.3 -> "1", 0.8 -> "1", 1.4 -> "1", 2.6 -> "3".
 *
 * A midpoint rounds up (1.25 onions -> "1½", 1.5 eggs -> "2") — the cook has to buy and use the
 * piece. Zero (or a non-finite number) is not rounded: nothing is invented where nothing is
 * asked for. Grams and millilitres keep the existing "at most one decimal" display.
 *
 * The internal word `count` never reaches the reader: it prints as "Stk." / "pcs", `clove` as
 * "Zehe(n)" / "clove(s)" (the catalog's own count nouns).
 */

type TFn = (key: string, vars?: Record<string, string | number>) => string
type Locale = "de" | "en"

/** Counted ingredients where a HALF is a normal kitchen amount (round to the nearest 0.5). */
export const HALVABLE_COUNT_INGREDIENTS: ReadonlySet<string> = new Set([
  "onion",
  "avocado",
  "apple",
  "banana",
  "lemon",
])

/** Counted ingredients that are used whole (round to the nearest whole piece). Listed so a new catalog item forces a decision. */
export const WHOLE_COUNT_INGREDIENTS: ReadonlySet<string> = new Set([
  "egg",
  "garlic",
  "wheat_tortilla",
])

/** Wire units that mean "pieces of the thing". `count` is the catalog's canonical unit; the rest are printable aliases. */
const PIECE_UNITS: ReadonlySet<string> = new Set([
  "count",
  "pcs",
  "Stk.",
  "piece",
  "pieces",
  "Stück",
])
/** Garlic's counted unit: the canonical count IS the clove. */
const CLOVE_UNIT = "clove"

const intl = (locale: Locale) => (locale === "de" ? "de-DE" : "en-DE")

/** Whether a wire unit is a counted unit (the ones this file rounds). */
export function isCountUnit(unit: string): boolean {
  return PIECE_UNITS.has(unit) || unit === CLOVE_UNIT
}

/**
 * The counted amount as it is shown: see the file comment. `ingredientId` decides halvable vs whole.
 * Returns the number unchanged for zero / negative / non-finite input.
 */
export function roundCountForDisplay(quantity: number, ingredientId: string): number {
  if (!Number.isFinite(quantity) || quantity <= 0) return quantity
  if (HALVABLE_COUNT_INGREDIENTS.has(ingredientId)) {
    return Math.max(0.5, Math.round(quantity * 2) / 2)
  }
  return Math.max(1, Math.round(quantity))
}

/** "2", "½", "1½" — a whole number, a half, or a whole number and a half. Anything else falls back to one decimal. */
function countAmountText(amount: number, locale: Locale): string {
  const whole = Math.floor(amount)
  if (amount - whole === 0.5)
    return whole === 0
      ? "½"
      : `${new Intl.NumberFormat(intl(locale), { maximumFractionDigits: 0 }).format(whole)}½`
  return new Intl.NumberFormat(intl(locale), { maximumFractionDigits: 1 }).format(amount)
}

/** Server-scaled g / ml quantity, at most one decimal — display formatting only, never scaling. */
function measuredAmountText(quantity: number, locale: Locale): string {
  return new Intl.NumberFormat(intl(locale), { maximumFractionDigits: 1 }).format(quantity)
}

/**
 * One ingredient line's quantity + unit, ready to print: "300 g", "1 Stk.", "1½ Stk.", "2 Zehen".
 * `line` is the served `RecipeIngredientLine` (or any object with those three fields).
 */
export function ingredientQuantityText(
  line: { ingredientId: string; quantity: number; unit: string },
  locale: Locale,
  t: TFn
): string {
  if (!isCountUnit(line.unit)) return `${measuredAmountText(line.quantity, locale)} ${line.unit}`
  const shown = roundCountForDisplay(line.quantity, line.ingredientId)
  const amount = countAmountText(shown, locale)
  if (line.unit === CLOVE_UNIT)
    return `${amount} ${t(shown === 1 ? "views.shop.countNoun.cloveOne" : "views.shop.countNoun.cloveMany")}`
  return `${amount} ${t("views.pantry.unitCount")}`
}
